import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { Plus, X, Filter, MapPin, User, Calendar, MessageCircle } from 'lucide-react';

const PROVINCES = ['SAN_JOSE', 'ALAJUELA', 'HEREDIA', 'CARTAGO', 'GUANACASTE', 'PUNTARENAS', 'LIMON'];

export default function WantsList() {
  const [wants, setWants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    brand: '',
    province: '',
    sort: 'newest'
  });

  useEffect(() => {
    fetchWants();
  }, []);

  const fetchWants = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/wants');
      setWants(data.wants || []);
    } catch {
      setWants([]);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ search: '', brand: '', province: '', sort: 'newest' });
  };

  // Get unique brands from loaded wants
  const allBrands = [...new Set(wants.map(w => w.category?.brand).filter(Boolean))].sort();

  // Filter wants based on current filters
  const filteredWants = wants.filter(want => {
    if (filters.brand && want.category?.brand !== filters.brand) return false;
    if (filters.province && want.province !== filters.province) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matchesTitle = want.title?.toLowerCase().includes(search);
      const matchesDesc = want.description?.toLowerCase().includes(search);
      if (!matchesTitle && !matchesDesc) return false;
    }
    return true;
  });

  // Sort wants
  const sortedWants = [...filteredWants].sort((a, b) => {
    if (filters.sort === 'price_asc') return (a.budget_min || 0) - (b.budget_min || 0);
    if (filters.sort === 'price_desc') return (b.budget_min || 0) - (a.budget_min || 0);
    if (filters.sort === 'newest') return new Date(b.created_at) - new Date(a.created_at);
    return 0;
  });

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white border-b sticky top-16 md:top-32 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">
                {filters.search ? `Resultados: "${filters.search}"` : 
                 filters.brand ? `${filters.brand} - Piezas Buscadas` : 
                 '🔍 Piezas que la gente busca'}
              </h1>
              <p className="text-sm text-gray-500">{sortedWants.length} solicitudes</p>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className="md:hidden flex items-center gap-2 px-4 py-2 border rounded-full"
              >
                <Filter className="w-4 h-4" /> Filtros
              </button>
              <Link 
                to="/wants/new" 
                className="hidden md:flex bg-primary text-white px-4 py-2 rounded-full items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Nueva solicitud
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar Filters */}
          <aside className={`${showFilters ? 'fixed inset-0 z-50 bg-white p-4 overflow-auto' : 'hidden'} md:block md:w-64 shrink-0`}>
            <div className="md:sticky md:top-40">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold">Filtros</h3>
                {(filters.search || filters.brand || filters.province) && (
                  <button onClick={clearFilters} className="text-sm text-primary">Limpiar</button>
                )}
              </div>

              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Buscar</label>
                <input 
                  type="text"
                  placeholder="Ej: bobina, escape..."
                  value={filters.search}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              {/* Brand */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Marca de moto</label>
                <select 
                  value={filters.brand}
                  onChange={(e) => updateFilter('brand', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Todas las marcas</option>
                  {allBrands.map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              {/* Province */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Ubicación</label>
                <select 
                  value={filters.province}
                  onChange={(e) => updateFilter('province', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Todas las provincias</option>
                  {PROVINCES.map(p => (
                    <option key={p} value={p}>{p.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>

              {showFilters && (
                <button 
                  onClick={() => setShowFilters(false)}
                  className="w-full bg-primary text-white py-3 rounded-full mt-4 md:hidden"
                >
                  Aplicar Filtros
                </button>
              )}
            </div>
          </aside>

          {/* Wants Grid */}
          <div className="flex-1">
            {/* Sort bar */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex gap-2 flex-wrap">
                {filters.brand && (
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm flex items-center gap-1">
                    {filters.brand}
                    <button onClick={() => updateFilter('brand', '')}><X className="w-3 h-3" /></button>
                  </span>
                )}
                {filters.province && (
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm flex items-center gap-1">
                    {filters.province.replace('_', ' ')}
                    <button onClick={() => updateFilter('province', '')}><X className="w-3 h-3" /></button>
                  </span>
                )}
                {filters.search && (
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm flex items-center gap-1">
                    "{filters.search}"
                    <button onClick={() => updateFilter('search', '')}><X className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
              <select 
                value={filters.sort}
                onChange={(e) => updateFilter('sort', e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="newest">Más recientes</option>
                <option value="price_asc">Precio: menor a mayor</option>
                <option value="price_desc">Precio: mayor a menor</option>
              </select>
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 flex gap-4">
                    <div className="w-16 h-16 bg-gray-200 rounded-lg animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : sortedWants.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-bold mb-2">No hay solicitudes</h3>
                <p className="text-gray-500 mb-4">Intenta con otros filtros o busca algo diferente.</p>
                <button onClick={clearFilters} className="bg-primary text-white px-6 py-2 rounded-full">
                  Limpiar Filtros
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {sortedWants.map(want => (
                  <WantListItem key={want.id} want={want} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile FAB */}
      <Link 
        to="/wants/new" 
        className="md:hidden fixed bottom-6 right-6 bg-primary text-white w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
      >
        <Plus className="w-6 h-6" />
      </Link>
    </div>
  );
}

// Item de lista para wants
function WantListItem({ want }) {
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diffDays = Math.ceil(Math.abs(now - d) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return d.toLocaleDateString('es-CR', { month: 'short', day: 'numeric' });
  };

  const images = want.images || [];
  const thumbnail = images.find(img => img.is_primary) || images[0];

  return (
    <div className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition">
      {/* Thumbnail Image */}
      <Link to={`/wants/${want.id}`} className="shrink-0">
        {thumbnail ? (
          <img 
            src={thumbnail.url} 
            alt={want.title}
            className="w-16 h-16 object-cover rounded-lg"
            onError={(e) => { e.target.onerror=null; e.target.style.display='none'; }}
          />
        ) : (
          <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center">
            <span className="text-2xl">🔍</span>
          </div>
        )}
      </Link>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <Link to={`/wants/${want.id}`} className="font-semibold truncate hover:text-primary transition block">
          {want.title}
        </Link>
        <div className="flex items-center gap-3 text-sm text-gray-500 mt-1 flex-wrap">
          {want.category && (
            <span className="bg-gray-100 px-2 py-0.5 rounded text-xs font-medium">
              {want.category.brand} {want.category.model}
            </span>
          )}
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {want.province?.replace('_', ' ')}
          </span>
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {want.user?.name}
          </span>
        </div>
      </div>

      {/* Budget */}
      <div className="text-right shrink-0">
        <p className="font-bold text-primary">
          ₡{want.budget_min?.toLocaleString('es-CR')}
          {want.budget_max && (
            <span className="text-xs font-normal text-gray-400"> - ₡{want.budget_max.toLocaleString('es-CR')}</span>
          )}
        </p>
        <p className="text-xs text-gray-400 flex items-center justify-end gap-1 mt-1">
          <Calendar className="w-3 h-3" />
          {formatDate(want.created_at)}
          {want.expires_at && (
            <span className={`ml-2 font-medium ${new Date(want.expires_at) - Date.now() < 3*24*60*60*1000 ? 'text-red-500' : 'text-gray-400'}`}>
              · vence {new Date(want.expires_at).toLocaleDateString('es-CR')}
            </span>
          )}
        </p>
      </div>

      {/* Action */}
      <Link 
        to={`/wants/${want.id}`}
        className="shrink-0 flex items-center gap-2 bg-yellow-400 border-2 border-black text-black hover:bg-yellow-500 px-4 py-2 rounded-lg text-sm font-bold transition"
      >
        <MessageCircle className="w-4 h-4" />
        <span className="hidden sm:inline">Contactar</span>
      </Link>
    </div>
  );
}